import { SalamanderPianoSynth, type Synth } from "@/lib/audio/synth";
import type { LiveEvent, TimedLiveEvent } from "@/lib/live/live-events";
import type { LiveSession } from "@/lib/live/live-session";
import type { LiveParticipant } from "@/lib/live/live-transport";
import { canEmit, type LiveSessionState } from "@/lib/live/session-state";
import { Note } from "@/lib/music/note";
import { Lane } from "../lane";
import { Instrument } from "../instrument";
import type { Transport } from "../transport";
import { BaseControler } from "./base-controler";
import type { LaneOrigin } from "./controler";

/**
 * Ce qu'un partant garde d'écran après le délai de restitution : la traîne de
 * ses dernières notes. Le piano relâche en 1,2 s (`SalamanderPianoSynth`), plus
 * une marge pour que la dernière image passe.
 */
const DEPARTURE_TAIL_MS = 1500;

/**
 * Ce que les autres jouent, une piste pour eux tous, une lane chacun.
 *
 * # Pourquoi une lane par participant
 *
 * La version précédente enfonçait les touches distantes sur le `Piano` local,
 * en `origin: "app"`. Ça sonnait, mais ça mélangeait trois choses : le jeu de
 * la personne, celui de son partenaire, et l'état du clavier à l'écran. On ne
 * pouvait ni couper le partenaire, ni le voir séparément sur le roll, ni savoir
 * qui avait joué quoi — et le piano n'a qu'une pédale pour tout le monde.
 *
 * Une lane par participant règle les trois d'un coup : c'est l'objet du studio
 * qui sait déjà se taire, se cacher, se dessiner d'une couleur et porter un
 * nom. Le partenaire devient une piste comme une autre.
 *
 * # Le tick, et pourquoi il fallait l'attendre
 *
 * Chaque geste arrive avec sa position sur le transport partagé. C'est elle
 * qu'on écrit dans la lane, pas l'instant de réception : une note jouée sur le
 * troisième temps se dessine sur le troisième temps, même si le réseau l'a fait
 * traîner. Sans horloges alignées ce tick ne voudrait rien dire, et c'est
 * pourquoi cette piste ne pouvait pas exister avant.
 *
 * Le son, lui, ne peut pas remonter le temps : `Scheduler` traite un geste dont
 * le tick est déjà passé en le jouant tout de suite. La note sonne donc en
 * retard du trajet réseau, mais se *dessine* au bon endroit. Les deux sont
 * justes, ils ne répondent simplement pas à la même question.
 *
 * # Les notes qui restent enfoncées
 *
 * Un partenaire qui ferme son onglet au milieu d'un accord, ou un note-off
 * perdu — le trafic passe en mode non fiable. D'où `held`, la mémoire de ce que
 * chacun tient : elle sert au départ d'un participant comme à la resynchro
 * périodique par `applyState`.
 *
 * Et un troisième cas, né des deux régimes : un leader **démis** au milieu
 * d'un accord. La session ignore désormais ses trames — c'est la garde de
 * réception —, donc le note-off qui fermerait son accord n'arrivera jamais.
 * On relâche donc ce qu'il tenait dès que l'état publié dit qu'il n'a plus le
 * droit d'émettre, comme pour un partant, la lane en moins : il est toujours
 * là, et peut reprendre le lead.
 */
export class LiveControler extends BaseControler {
  label = "Live";

  private readonly lanes = new Map<string, Lane>();
  private readonly names = new Map<string, string>();
  private readonly held = new Map<string, Set<number>>();

  /**
   * La pédale de chacun, telle qu'on l'a appliquée.
   *
   * Tenue pour la même raison que `held` : une trame d'état décrit ce que le
   * pair tient, et il n'y a rien à appliquer de ce qui n'a pas changé. Sans
   * cette mémoire, la pédale était réécrite dans la lane du pair **quatre fois
   * par seconde** pendant toute la séance — une trame d'état la déclare
   * toujours, même levée.
   */
  private readonly sustained = new Map<string, boolean>();
  private readonly releases: (() => void)[] = [];

  /**
   * Un synthé prêt pour chaque participant qui peut jouer, avant son premier
   * geste.
   *
   * La lane, elle, n'est créée qu'au premier geste — voir `laneFor` : pas de
   * lanes muettes pour ceux qui écoutent. Mais un synthé a besoin de temps :
   * monter sa couche d'échantillons passe par plusieurs tours de boucle même
   * quand le cache a déjà tout décodé, et `noteOn` reste muet tant qu'elle n'y
   * est pas. Créé au premier geste, il manquait donc ce geste — le premier
   * accord d'un partenaire ne sonnait jamais. Créé dès que l'état publié dit
   * qu'il peut jouer, il est chaud bien avant. Voir `warmUp` pour qui y a
   * droit.
   */
  private readonly warm = new Map<string, Synth>();

  /** Les partants dont la lane reste le temps que leurs notes tombent. */
  private readonly leaving = new Map<string, ReturnType<typeof setTimeout>>();

  /** Un canal par lane, pour ne pas les confondre. Le premier est au piano. */
  private nextChannel = 1;

  constructor(
    private readonly session: LiveSession,
    private readonly transport: Transport,
    private readonly context: AudioContext,
    /**
     * Le délai de restitution de notre vue, en secondes, lu au moment d'un
     * départ. Zéro par défaut : un partant s'en va tout de suite.
     */
    private readonly playoutDelaySeconds: () => number = () => 0,
  ) {
    super();
  }

  start(): void {
    this.releases.push(
      this.session.onRemoteEvents((events, from) => this.applyEvents(events, from)),
      this.session.onRemoteState((events, from) => this.applyState(events, from)),
      this.session.onParticipantLeft((participant) => this.forget(participant)),
      this.session.sessionState.subscribe((state) => this.onSessionState(state)),
      this.session.participants.subscribe((participants) => this.warmUp(participants)),
    );

    // Ceux qui sont déjà là : l'abonnement ne rejoue pas la valeur courante.
    this.warmUp(this.session.participants.value);
  }

  destroy(): void {
    for (const release of this.releases) {
      release();
    }

    this.releases.length = 0;

    for (const timer of this.leaving.values()) {
      clearTimeout(timer);
    }

    this.leaving.clear();

    // Tout de suite, sans la traîne de `forget` : c'est la séance entière qui
    // s'en va, et personne ne reste pour regarder tomber ses dernières notes.
    const tick = this.transport.currentTick();

    for (const id of Array.from(this.lanes.keys())) {
      this.releaseHeld(id, tick);
      this.removeLane(id);
    }

    for (const synth of this.warm.values()) {
      synth.dispose();
    }

    this.warm.clear();

    super.destroy();
  }

  /** La lane d'un participant, si elle existe déjà. */
  laneOf(participantId: string): Lane | null {
    return this.lanes.get(participantId) ?? null;
  }

  getLaneOrigin(lane: Lane): LaneOrigin | undefined {
    for (const [participantId, candidate] of this.lanes) {
      if (candidate === lane) {
        return {
          kind: "live-peer",
          participantId,
          name: this.names.get(participantId) ?? "",
        };
      }
    }

    return undefined;
  }

  // ── Réseau → lane ────────────────────────────────────────────

  private applyEvents(events: readonly TimedLiveEvent[], from: LiveParticipant): void {
    const lane = this.laneFor(from);

    for (const timed of events) {
      this.applyOne(lane, timed.event, timed.tick, from.id);
    }
  }

  /**
   * Aligne une lane sur ce qu'un pair déclare tenir.
   *
   * Ce n'est pas une suite de gestes à rejouer mais un état à atteindre : on
   * enfonce ce qui manque, on relâche ce qui est en trop. C'est cette
   * idempotence qui fait du procédé un filet — le réappliquer dix fois de suite
   * ne produit aucun son parasite, alors que réémettre des note-off couperait
   * une note rejouée entre-temps.
   *
   * Les corrections portent le tick d'**maintenant** : une trame d'état décrit
   * un instant, pas une position dans le morceau, et elle n'en transporte donc
   * aucune.
   */
  private applyState(events: readonly LiveEvent[], from: LiveParticipant): void {
    const declared = new Map<number, number>();
    let sustain: boolean | null = null;

    for (const event of events) {
      if (event.kind === "noteOn") {
        declared.set(event.midi, event.velocity);
      } else if (event.kind === "sustain") {
        sustain = event.down;
      }
    }

    const lane = this.laneFor(from);
    const held = this.heldBy(from.id);
    const tick = this.transport.currentTick();

    for (const midi of Array.from(held)) {
      if (!declared.has(midi)) {
        this.applyOne(lane, { kind: "noteOff", midi }, tick, from.id);
      }
    }

    for (const [midi, velocity] of declared) {
      if (!held.has(midi)) {
        this.applyOne(lane, { kind: "noteOn", midi, velocity }, tick, from.id);
      }
    }

    // Comme les notes : on n'applique que ce qui diffère. La pédale figure dans
    // *toutes* les trames d'état — c'est ce qui rend le filet complet — donc la
    // réappliquer sans comparer insérait un événement de pédale par trame dans
    // la lane du pair, sans fin, et relançait une passe de relâchement à chaque
    // fois.
    if (sustain !== null && (this.sustained.get(from.id) ?? false) !== sustain) {
      this.applyOne(lane, { kind: "sustain", down: sustain }, tick, from.id);
    }
  }

  private applyOne(lane: Lane, event: LiveEvent, tick: number, participantId: string): void {
    if (event.kind === "sustain") {
      // Mémorisée ici et non chez l'appelant : un geste et une trame d'état
      // passent tous les deux par là, et c'est l'état *appliqué* qu'on compare.
      this.sustained.set(participantId, event.down);

      if (event.down) {
        lane.sustainOn(tick);
      } else {
        lane.sustainOff(tick);
      }

      return;
    }

    const note = safeNote(event.midi);

    if (note === null) {
      return;
    }

    if (event.kind === "noteOn") {
      this.heldBy(participantId).add(event.midi);
      lane.noteOn(note, tick, event.velocity);

      return;
    }

    this.heldBy(participantId).delete(event.midi);
    lane.noteOff(note, tick);
  }

  // ── Cycle de vie des lanes ───────────────────────────────────

  /**
   * La lane d'un participant, créée à son premier geste.
   *
   * À l'arrivée plutôt qu'au premier geste, la piste se remplirait de lanes
   * muettes pour des gens qui écoutent. Au premier geste, elle ne contient que
   * ceux qui jouent.
   */
  private laneFor(participant: LiveParticipant): Lane {
    const existing = this.lanes.get(participant.id);

    this.names.set(participant.id, participant.name);

    if (existing !== undefined) {
      // Revenu pendant que ses dernières notes tombaient : il retrouve sa lane.
      this.cancelLeaving(participant.id);

      return existing;
    }

    // Un synthé par participant, et non un partagé : deux personnes qui
    // appuient sur le même do doivent pouvoir le relâcher indépendamment.
    // Celui préparé à son arrivée, s'il y en a un — voir `warm`.
    const synth = this.warm.get(participant.id) ?? new SalamanderPianoSynth(this.context);
    this.warm.delete(participant.id);

    const lane = new Lane(
      this.nextChannel,
      synth,
      new Instrument("Piano", participant.name),
      // Ses ticks viennent du transport partagé, pas de notre vue.
      true,
    );

    this.nextChannel += 1;
    this.lanes.set(participant.id, lane);
    this.emit("lane:created", lane);

    return lane;
  }

  /**
   * Un état publié change deux choses, et les deux se lisent sur `canEmit` :
   * qui doit être relâché, et qui mérite un synthé.
   */
  private onSessionState(state: LiveSessionState | null): void {
    this.silenceDemoted(state);
    this.warmUp(this.session.participants.value);
  }

  /**
   * Relâche ce que tient quiconque vient de perdre le droit d'émettre — ou tout
   * le monde, quand le rideau tombe.
   *
   * Appelé à chaque état publié, pas seulement sur un changement de lead : la
   * question « qui a encore le droit » se répond sur l'état entier — un jam
   * qui s'éteint démet tout le monde sauf le leader —, et relâcher ce qui ne
   * l'est plus est idempotent. Voir « Les notes qui restent enfoncées ».
   *
   * Le rideau y entre parce qu'il crée exactement la même situation : la
   * réception écarte tout ce qui vient d'en face, note-off compris, donc une
   * touche enfoncée à l'instant où il tombe resterait enfoncée pour toujours.
   * Personne n'émet derrière un rideau baissé, et personne ne tient rien.
   */
  private silenceDemoted(state: LiveSessionState | null): void {
    const tick = this.transport.currentTick();
    const curtain = state?.curtain === true;

    for (const participantId of Array.from(this.lanes.keys())) {
      if (curtain || !canEmit(state, participantId)) {
        this.releaseHeld(participantId, tick);
      }
    }
  }

  /**
   * Relâche tout ce qu'un participant tient : ses touches, puis sa pédale.
   *
   * La pédale aussi, et après les touches : un relâchement sous pédale est
   * retenu par l'ordonnanceur jusqu'au `sustainOff`. Sans elle, un partant qui
   * tenait la pédale laissait ses notes sonner malgré les note-off. Tout passe
   * par `applyOne`, pour que `held` et `sustained` restent la vérité de ce qui
   * a été appliqué.
   */
  private releaseHeld(participantId: string, tick: number): void {
    const lane = this.lanes.get(participantId);

    if (lane === undefined) {
      return;
    }

    for (const midi of Array.from(this.heldBy(participantId))) {
      this.applyOne(lane, { kind: "noteOff", midi }, tick, participantId);
    }

    if (this.sustained.get(participantId) === true) {
      this.applyOne(lane, { kind: "sustain", down: false }, tick, participantId);
    }
  }

  /**
   * Prépare un synthé pour chaque participant qui peut émettre et n'a encore
   * ni lane ni synthé.
   *
   * # Seulement ceux qui peuvent jouer
   *
   * Un synthé coûte une couche d'échantillons ; en préparer un par arrivant en
   * préparait cinquante à l'interprète d'un récital, pour cinquante
   * spectateurs dont aucun ne fera jamais sonner une note. On ne chauffe donc
   * que ceux que l'état publié laisse émettre — le leader, ou tout le monde en
   * jam —, et on refait le tour à chaque état : un nouveau leader est chauffé
   * à l'état qui le nomme, avant son premier geste.
   *
   * Sans état publié, personne : on ne sait pas qui joue, et le premier état
   * arrive à la jointure, avant qu'aucun geste ne puisse circuler — la garde
   * de réception ne laisse de toute façon passer les trames de personne tant
   * qu'il n'y a pas d'état.
   *
   * Celui qui perd le droit d'émettre garde son synthé chaud : il peut
   * reprendre le lead, et le jeter pour le recréer à ce moment-là coûterait
   * exactement le temps qu'on cherche à gagner.
   */
  private warmUp(participants: readonly LiveParticipant[]): void {
    const state = this.session.sessionState.value;

    for (const participant of participants) {
      if (this.lanes.has(participant.id) || this.warm.has(participant.id)) {
        continue;
      }

      if (!canEmit(state, participant.id)) {
        continue;
      }

      this.warm.set(participant.id, new SalamanderPianoSynth(this.context));
    }
  }

  /**
   * Relâche ce qu'un partant tenait, puis retire sa lane, une fois ses
   * dernières notes tombées.
   *
   * # Pourquoi attendre
   *
   * Ses notes portent le tick où il les a jouées, et notre tête de lecture
   * court un délai de restitution derrière : quand il part, les dernières sont
   * encore devant elle, dessinées au-dessus de la ligne et confiées au synthé
   * pour plus tard. Retirer la lane tout de suite démonte ce synthé et efface
   * ces notes. Avec les quelques millisecondes d'un joueur, personne ne le
   * voyait. Avec les quatre secondes d'un spectateur de récital, c'était la fin
   * du récital qui disparaissait. La lane reste donc le temps du délai, plus la
   * traîne du son.
   *
   * Un retour pendant l'attente, une liaison qui tombe et revient, retrouve la
   * lane telle quelle : voir `laneFor`.
   */
  private forget(participant: LiveParticipant): void {
    // Un partant qui n'a jamais joué ne laisse qu'un synthé chaud.
    this.warm.get(participant.id)?.dispose();
    this.warm.delete(participant.id);

    if (!this.lanes.has(participant.id)) {
      return;
    }

    // Au présent de la séance, comme tous ses gestes : ses relâchements tombent
    // chez nous un délai plus tard, à la suite de ce qu'il avait joué.
    this.releaseHeld(participant.id, this.transport.currentTick());

    const lingerMs = this.playoutDelaySeconds() * 1000;

    if (lingerMs <= 0) {
      this.removeLane(participant.id);

      return;
    }

    this.cancelLeaving(participant.id);
    this.leaving.set(
      participant.id,
      setTimeout(() => {
        this.leaving.delete(participant.id);
        this.removeLane(participant.id);
      }, lingerMs + DEPARTURE_TAIL_MS),
    );
  }

  /** Retire la lane d'un participant, sans attendre. */
  private removeLane(participantId: string): void {
    const lane = this.lanes.get(participantId);

    if (lane === undefined) {
      return;
    }

    this.held.delete(participantId);
    this.sustained.delete(participantId);
    this.lanes.delete(participantId);
    this.names.delete(participantId);
    this.emit("lane:removed", lane);
  }

  /** Annule le retrait différé d'une lane, s'il y en a un. */
  private cancelLeaving(participantId: string): void {
    const timer = this.leaving.get(participantId);

    if (timer === undefined) {
      return;
    }

    clearTimeout(timer);
    this.leaving.delete(participantId);
  }

  private heldBy(participantId: string): Set<number> {
    const existing = this.held.get(participantId);

    if (existing !== undefined) {
      return existing;
    }

    const created = new Set<number>();
    this.held.set(participantId, created);

    return created;
  }
}

/** `Note.fromMidi` lève sur une valeur hors gamme ; une trame bien formée peut
 *  malgré tout porter un numéro qu'aucun piano ne couvre. */
function safeNote(midi: number): Note | null {
  try {
    return Note.fromMidi(midi);
  } catch {
    return null;
  }
}
